import { Injectable } from '@angular/core';
import { AccountsResourceBaseUrlBuilder } from '@core/accounts-namespaced-resource-url-builder';
import { ApiEntityService } from '@core/api/api-entity.service';
import { Automapper } from '@core/api/mapper';

import { PartnerState, PartnerStore } from './partner.store';
import { documentCategory, Partner } from '@core/models/partner.model';
import { Observable } from 'rxjs';
import { DownloaderService } from '@core/downloader/downloader.service';
import { mergeMap } from 'rxjs/operators';
import { convertBlobToBase64 } from '@core/utils';
import { ApiService } from '@core/api/api.service';
import { Organization } from '@core/models/organization.model';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { UserOverview } from '@core/models/user-overview.model';
import { Language } from '@core/models/partner.model';
import { EnvConfig } from '@core/config';
import { map } from 'rxjs/operators';
import {
  PartnerBillingSettings,
  PartnerGpAddonPriceItem,
  PartnerLpTierPriceItem,
  PartnerStripeStatus,
} from '@core/models/partner-billing.model';

@Injectable({ providedIn: 'root' })
export class PartnerService extends ApiEntityService<PartnerState> {
  constructor(
    store: PartnerStore,
    urlBuilder: AccountsResourceBaseUrlBuilder,
    private downloader: DownloaderService,
    private apiService: ApiService,
    private env: EnvConfig,
  ) {
    super(store, urlBuilder, Automapper.create(Partner));
    this.overrideResourceName('partners');
  }

  private get appUrl(): string {
    return this.env.aiEngineApiUrl || '';
  }

  private getHeaders() {
    return {
      headers: new HttpHeaders({
        'accept': 'application/json',
        'Content-Type': 'application/json'
      })
    };
  }

  private pick<T = any>(source: any, snakeKey: string, camelKey: string, defaultValue?: T): T {
    if (!source) {
      return defaultValue as T;
    }
    const snakeValue = source[snakeKey];
    if (snakeValue !== undefined) {
      return snakeValue;
    }
    const camelValue = source[camelKey];
    if (camelValue !== undefined) {
      return camelValue;
    }
    return defaultValue as T;
  }

  public getPartnerLogo(imageUrl: string): Observable<string | ArrayBuffer> {
    if (imageUrl) {
      return this.downloader.download(imageUrl).pipe(mergeMap((blob) => convertBlobToBase64(blob)));
    }
  }

  assignOrganization(organization: Organization, partner): Observable<unknown> {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    return this.apiService.patch(`${baseUrl}/partners/${partner.uid}/update`, {
      connect: 'organizations',
      org_type: organization.type,
      organization: organization.id,
    });
  }
  unAssignOrganization(organization: Organization, partner): Observable<unknown> {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    return this.apiService.patch(`${baseUrl}/partners/${partner.uid}/update`, {
      remove: 'organizations',
      org_type: organization.type,
      organization: organization.id,
    });
  }

  registerAdmin(admin, partner, isAnalyst: boolean = false): Observable<unknown> {
    const formData = new FormData();
    formData.append('email', admin.email);
    formData.append('firstName', admin.firstName);
    formData.append('lastName', admin.lastName);
    formData.append('salutation', admin.salutation);
    formData.append('title', admin.title);
    formData.append('partner', partner.uid);
    if (isAnalyst) {
      formData.append('isAnalystUser', 'true');
    }
    return this.apiService.post('accounts/partneradmins/register', formData);
  }

  getPartnerAdmins(
    id: string,
    params?: { pageSize?: number; page?: number; search?: string },
  ): Observable<UserOverview[]> {
    const safeParams = params || {};
    let httpParams = new HttpParams()
      .set('pageSize', safeParams.pageSize ? safeParams.pageSize.toString() : '10')
      .set('page', safeParams.page ? safeParams.page.toString() : '1')
      .set('search', safeParams.search || '');

    return this.apiService.get<UserOverview[]>(`accounts/partners/${id}/admins`, { params: httpParams });
  }

  getUsersToBeNotified(id: string): Observable<UserOverview[]> {
    return this.apiService.get<UserOverview[]>(`accounts/partners/${id}/notify`);
  }

  getPartnerSelfRegisterationConfig(id: string): Observable<any> {
    return this.apiService.get<any>(`accounts/partners/${id}/registration-config`);
  }

  /**
   * Returns the selling-region catalog for a partner: a record of
   * region code -> ISO-3166-1 alpha-2 country codes.
   */
  public getSellingRegionCountries(partnerUid: string): Observable<Record<string, string[]>> {
    return this.apiService.get<Record<string, string[]>>(
      `accounts/partners/${partnerUid}/selling-region-countries`,
    );
  }

  updatePartnerSelfRegisterationConfig(id, payload): Observable<any> {
    return this.apiService.put<any>(`accounts/partners/${id}/registration-config`, payload);
  }

  assignLanguage(language: Language, partner: Partner): Observable<unknown> {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    const currentLanguages = partner.availableLanguages || [];
    const updatedLanguages = [...currentLanguages.filter((lang) => lang?.code).map((lang) => lang.code), language.code];
    return this.apiService.patch(`${baseUrl}/partners/${partner.uid}/update`, {
      availableLanguages: updatedLanguages,
    });
  }

  unassignLanguage(language: Language, partner: Partner): Observable<unknown> {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    const currentLanguages = partner.availableLanguages || [];
    const updatedLanguages = currentLanguages
      .filter((lang) => lang?.code)
      .map((lang) => lang.code)
      .filter((code) => code !== language.code);
    return this.apiService.patch(`${baseUrl}/partners/${partner.uid}/update`, {
      availableLanguages: updatedLanguages,
    });
  }

  // ===========================================
  // 📌 INSERT PARTNER DOCUMENT CATEGORY
  // ===========================================
  public insertPartnerDocumentCategory(payload: documentCategory): Observable<any> {
    return this.apiService.post(this.appUrl + "/insert_partner_document_category", payload, this.getHeaders());
  }

  public getPartnerBillingSettings(partnerUid: string): Observable<PartnerBillingSettings> {
    return this.apiService.get<any>(`accounts/partners/${partnerUid}/billing-settings`).pipe(
      map((response) => ({
        uid: this.pick(response, 'uid', 'uid', ''),
        partnerUid: this.pick(response, 'partner_uid', 'partnerUid', ''),
        isEnabled: Boolean(this.pick(response, 'is_enabled', 'isEnabled', false)),
        lpTierPriceOverrides: this.pick(response, 'lp_tier_price_overrides', 'lpTierPriceOverrides', {}) || {},
        gpAddonPriceOverrides: this.pick(response, 'gp_addon_price_overrides', 'gpAddonPriceOverrides', {}) || {},
        stripeCustomerId: this.pick(response, 'stripe_customer_id', 'stripeCustomerId', null),
        lastSyncedAt: this.pick(response, 'last_synced_at', 'lastSyncedAt', null),
        syncStatus: this.pick(response, 'sync_status', 'syncStatus', '') || '',
        syncError: this.pick(response, 'sync_error', 'syncError', '') || '',
      })),
    );
  }

  public updatePartnerBillingSettings(partnerUid: string, isEnabled: boolean): Observable<PartnerBillingSettings> {
    return this.apiService.patch<any>(`accounts/partners/${partnerUid}/billing-settings`, { is_enabled: isEnabled }).pipe(
      map((response) => ({
        uid: this.pick(response, 'uid', 'uid', ''),
        partnerUid: this.pick(response, 'partner_uid', 'partnerUid', ''),
        isEnabled: Boolean(this.pick(response, 'is_enabled', 'isEnabled', false)),
        lpTierPriceOverrides: this.pick(response, 'lp_tier_price_overrides', 'lpTierPriceOverrides', {}) || {},
        gpAddonPriceOverrides: this.pick(response, 'gp_addon_price_overrides', 'gpAddonPriceOverrides', {}) || {},
        stripeCustomerId: this.pick(response, 'stripe_customer_id', 'stripeCustomerId', null),
        lastSyncedAt: this.pick(response, 'last_synced_at', 'lastSyncedAt', null),
        syncStatus: this.pick(response, 'sync_status', 'syncStatus', '') || '',
        syncError: this.pick(response, 'sync_error', 'syncError', '') || '',
      })),
    );
  }

  public getPartnerLpTierPrices(partnerUid: string): Observable<PartnerLpTierPriceItem[]> {
    return this.apiService.get<any>(`accounts/partners/${partnerUid}/billing/lp-tier-prices`).pipe(
      map((response) => {
        const prices = response?.prices || {};
        return Object.keys(prices).map((code) => ({
          code,
          tierName: this.pick(prices[code], 'tier_name', 'tierName', ''),
          annualCents: Number(this.pick(prices[code], 'annual_cents', 'annualCents', 0)),
          monthlyCents:
            this.pick(prices[code], 'monthly_cents', 'monthlyCents', null) != null
              ? Number(this.pick(prices[code], 'monthly_cents', 'monthlyCents', 0))
              : null,
          isOverride: Boolean(this.pick(prices[code], 'is_override', 'isOverride', false)),
        }));
      }),
    );
  }

  public savePartnerLpTierPrices(partnerUid: string, prices: PartnerLpTierPriceItem[]): Observable<PartnerLpTierPriceItem[]> {
    const lpTierPriceOverrides = prices.reduce(
      (acc, item) => ({
        ...acc,
        [item.code]: {
          annual_cents: Number(item.annualCents ?? 0),
          monthly_cents: Number(item.monthlyCents ?? 0),
        },
      }),
      {},
    );

    return this.apiService
      .put<any>(`accounts/partners/${partnerUid}/billing/lp-tier-prices`, { lp_tier_price_overrides: lpTierPriceOverrides })
      .pipe(
        map((response) => {
          const updated = response?.prices || {};
          return Object.keys(updated).map((code) => ({
            code,
            tierName: this.pick(updated[code], 'tier_name', 'tierName', ''),
            annualCents: Number(this.pick(updated[code], 'annual_cents', 'annualCents', 0)),
            monthlyCents:
              this.pick(updated[code], 'monthly_cents', 'monthlyCents', null) != null
                ? Number(this.pick(updated[code], 'monthly_cents', 'monthlyCents', 0))
                : null,
            isOverride: Boolean(this.pick(updated[code], 'is_override', 'isOverride', false)),
          }));
        }),
      );
  }

  public getPartnerGpAddonPrices(partnerUid: string): Observable<PartnerGpAddonPriceItem[]> {
    return this.apiService.get<any>(`accounts/partners/${partnerUid}/billing/gp-addon-prices`).pipe(
      map((response) => {
        const prices = response?.prices || {};
        return Object.keys(prices).map((code) => ({
          code,
          addonName: this.pick(prices[code], 'addon_name', 'addonName', ''),
          monthlyCents: Number(this.pick(prices[code], 'monthly_cents', 'monthlyCents', 0)),
          isOverride: Boolean(this.pick(prices[code], 'is_override', 'isOverride', false)),
        }));
      }),
    );
  }

  public savePartnerGpAddonPrices(partnerUid: string, prices: PartnerGpAddonPriceItem[]): Observable<PartnerGpAddonPriceItem[]> {
    const gpAddonPriceOverrides = prices.reduce(
      (acc, item) => ({
        ...acc,
        [item.code]: {
          monthly_cents: Number(item.monthlyCents ?? 0),
        },
      }),
      {},
    );

    return this.apiService
      .put<any>(`accounts/partners/${partnerUid}/billing/gp-addon-prices`, { gp_addon_price_overrides: gpAddonPriceOverrides })
      .pipe(
        map((response) => {
          const updated = response?.prices || {};
          return Object.keys(updated).map((code) => ({
            code,
            addonName: this.pick(updated[code], 'addon_name', 'addonName', ''),
            monthlyCents: Number(this.pick(updated[code], 'monthly_cents', 'monthlyCents', 0)),
            isOverride: Boolean(this.pick(updated[code], 'is_override', 'isOverride', false)),
          }));
        }),
      );
  }

  public syncPartnerBillingWithStripe(partnerUid: string): Observable<any> {
    return this.apiService.post<any>(`accounts/partners/${partnerUid}/billing/sync-stripe`, {});
  }

  public getPartnerBillingStripeStatus(partnerUid: string): Observable<PartnerStripeStatus> {
    return this.apiService.get<any>(`accounts/partners/${partnerUid}/billing/stripe-status`).pipe(
      map((response) => ({
        partnerUid: this.pick(response, 'partner_uid', 'partnerUid', ''),
        hasBillingSettings: Boolean(this.pick(response, 'has_billing_settings', 'hasBillingSettings', false)),
        isEnabled: this.pick(response, 'is_enabled', 'isEnabled'),
        stripeCustomerId: this.pick(response, 'stripe_customer_id', 'stripeCustomerId', null),
        lastSyncedAt: this.pick(response, 'last_synced_at', 'lastSyncedAt', null),
        syncStatus: this.pick(response, 'sync_status', 'syncStatus', '') || '',
        subscriptionCount: Number(this.pick(response, 'subscription_count', 'subscriptionCount', 0)),
      })),
    );
  }
}
